import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Building, Key, Lock, Info } from 'lucide-react';
import axios from 'axios';
import Toast from './Toast';
import '../styles/UnifiedAuth.css';
import { API_URL } from '../api/config';
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [restaurantId, setRestaurantId] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  const handleRequestOTP = async () => {
    if (!email || !restaurantId) {
      showToast('Please enter both email and restaurant ID', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/password/forgot`,
        { email, restaurantId }
      );

      if (response.data.success) {
        showToast('OTP has been sent to your email');
        setStep(2);
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to send OTP', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!otp || !newPassword || !confirmPassword) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/password/reset`,
        {
          email,
          restaurantId,
          otp,
          newPassword
        }
      );

      if (response.data.success) {
        showToast('Password has been reset successfully');
        setTimeout(() => navigate('/signin'), 1500);
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to reset password', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="unified-auth-container">
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      
      <div className="auth-form-wrapper">
        <h1 style={{ textAlign: 'center', color: '#8B4513', marginBottom: '5px' }}>TOBO</h1>
        <p style={{ textAlign: 'center', color: '#CD853F', marginBottom: '20px' }}>BILL IT, DONE!</p>

        {step === 1 ? (
          <>
            <p style={{ textAlign: 'center', marginBottom: '20px' }}>
              Enter your email and restaurant ID to receive a password reset code
            </p>

            <div className="form-group">
              <div style={{ position: 'relative' }}>
                <Mail style={{ position: 'absolute', left: '12px', top: '12px', color: '#8B4513' }} size={20} />
                <input
                  style={{ paddingLeft: '40px' }}
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                />
              </div>
            </div>

            <div className="form-group">
              <div style={{ position: 'relative' }}>
                <Building style={{ position: 'absolute', left: '12px', top: '12px', color: '#8B4513' }} size={20} />
                <input
                  style={{ paddingLeft: '40px' }}
                  placeholder="Restaurant ID"
                  value={restaurantId}
                  onChange={(e) => setRestaurantId(e.target.value)}
                />
              </div>
            </div>

            {/* Enhanced Forgot Restaurant ID button using Tailwind and lucide-react icon */}
            <div className="flex justify-center mt-4">
              <a 
                href="/info" 
                className="flex items-center text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
              >
                <Info size={16} className="mr-1" />
                Forgot Restaurant ID?
              </a>
            </div>

            <button
              className="submit-btn"
              onClick={handleRequestOTP}
              disabled={loading}
              style={{ opacity: loading ? 0.7 : 1, marginTop: '20px' }}
            >
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>

            <div className="auth-links" style={{ justifyContent: 'center' }}>
              <span onClick={() => navigate('/signin')}>
                Back to Sign In
              </span>
            </div>
          </>
        ) : (
          <>
            <p style={{ textAlign: 'center', marginBottom: '20px' }}>
              Enter the code sent to your email and your new password
            </p>

            <div className="form-group">
              <div style={{ position: 'relative' }}>
                <Key style={{ position: 'absolute', left: '12px', top: '12px', color: '#8B4513' }} size={20} />
                <input
                  style={{ paddingLeft: '40px' }}
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  type="number"
                />
              </div>
            </div>

            <div className="form-group">
              <div style={{ position: 'relative' }}>
                <Lock style={{ position: 'absolute', left: '12px', top: '12px', color: '#8B4513' }} size={20} />
                <input
                  style={{ paddingLeft: '40px' }}
                  placeholder="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <div style={{ position: 'relative' }}>
                <Lock style={{ position: 'absolute', left: '12px', top: '12px', color: '#8B4513' }} size={20} />
                <input
                  style={{ paddingLeft: '40px' }}
                  placeholder="Confirm New Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>

            <button
              className="submit-btn"
              onClick={handleResetPassword}
              disabled={loading}
              style={{ opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <div className="auth-links" style={{ justifyContent: 'center' }}>
              <span
                onClick={() => {
                  setStep(1);
                  setOtp('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
              >
                Back to previous step
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
